import { Column, ErrorText, LoadingIndicator, Row, Text } from "@/components";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent } from "@/components/ui/dialog";

import { useDeleteRemoveUser } from "@/hooks/deleteRemoves/useDeleteRemoveUser";
import { mixpanelUtils } from "@/lib/mixpanel/mixpanel";
import { MIXPANEL_SCREENS } from "@/lib/mixpanel/types";
import { User } from "@/types";

export const DeleteUserDialog = ({
  isOpen,
  setIsOpen,
  user,
}: {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  user: User | null;
}) => {
  /* Database CRUD Operations */
  // Delete
  const {
    mutate: deleteUser,
    isPending,
    isError,
    reset,
  } = useDeleteRemoveUser();

  /* Handlers */
  const handleDelete = () => {
    if (!user) return;
    mixpanelUtils.trackButtonClick("Delete user", MIXPANEL_SCREENS.USERS);
    deleteUser(
      { id: user.id },
      { onSuccess: () => { setIsOpen(false); } },
    );
  };

  /* Render */
  return (
    <Dialog
      open={isOpen}
      onOpenChange={(open) => {
        reset();
        setIsOpen(open);
      }}
    >
      <DialogContent className="sm:max-w-md">
        <Column className="gap-1">
          <p className="text-2xl font-semibold text-slate-700">Delete user</p>
          <Text className="font-normal" variant="secondary">
            Are you sure you want to delete{" "}
            <span className="font-semibold">
              {user?.firstName} {user?.lastName}
            </span>
            ? This action cannot be undone.
          </Text>
        </Column>
        <Column className="gap-2">
          <Row className="items-center justify-end gap-2">
            <Button
              className="rounded-full"
              type="button"
              onClick={() => { setIsOpen(false); }}
              variant="secondary"
            >
              Cancel
            </Button>
            <Button
              className="relative rounded-full"
              disabled={isPending}
              onClick={handleDelete}
              variant="destructive"
            >
              <span className={isPending ? "invisible" : ""}>Delete</span>
              {isPending && (
                <LoadingIndicator
                  className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2"
                  gearColor="#fff"
                  variant="small"
                />
              )}
            </Button>
          </Row>
          {isError && (
            <ErrorText errorMessage="Failed to delete user. Please try again." />
          )}
        </Column>
      </DialogContent>
    </Dialog>
  );
};
